import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import GoogleLoginButton from "@/components/GoogleLoginButton";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register(name, email, password);
      toast.success(`Welcome, ${u.name}. Pick a plan to get full access.`);
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-background paper-texture">
      <form onSubmit={handleSubmit} className="w-full max-w-md space-y-6" data-testid="register-form">
        <div>
          <div className="label-caps mb-2">Create account</div>
          <h2 className="font-serif text-4xl">Start learning.</h2>
        </div>
        <div className="space-y-4">
          <GoogleLoginButton label="Sign up with Google" testId="google-signup-btn" />
          <div className="relative">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
            <div className="relative flex justify-center text-[10px] uppercase tracking-widest">
              <span className="bg-background px-3 text-muted-foreground">or use email</span>
            </div>
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="name">Full name</Label>
          <Input id="name" data-testid="register-name" value={name} onChange={e => setName(e.target.value)} required />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" data-testid="register-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password (min 8 chars)</Label>
          <Input id="password" data-testid="register-password" type="password" minLength={8} value={password} onChange={e => setPassword(e.target.value)} required />
        </div>
        <Button type="submit" data-testid="register-submit" disabled={loading} className="w-full">
          {loading ? "Creating…" : "Create account"}
        </Button>
        <p className="text-sm text-muted-foreground">
          Already have an account? <Link data-testid="go-login" to="/login" className="underline">Sign in</Link>
        </p>
        <div className="pt-4 border-t border-border">
          <div className="rounded-md border border-accent/40 bg-accent/5 p-4">
            <div className="label-caps text-accent mb-1">Signing up to teach?</div>
            <p className="text-sm text-muted-foreground mb-3">
              Create your account here, then unlock the Instructor plan on the pricing page — you'll be promoted to an instructor account automatically.
            </p>
            <Link
              to="/pricing"
              data-testid="signup-as-instructor-link"
              className="text-sm underline text-foreground"
            >
              See the instructor plan →
            </Link>
          </div>
        </div>
      </form>
    </div>
  );
}
